// @flow
/** @jsx jsx */

import { Component, createRef } from 'react';

import { Colors } from '@zapier/theme';
import { jsx } from '@emotion/core';
import { Icon, Text } from '@zapier/common-components';

import Button from 'app/common/components/Button';

import type { Ref } from 'app/common/types';

type Props = {
  helpText: string,
};

type State = { isExpanded: boolean, shouldBeExpandable: boolean };

const hintColor = 'steel';

const containerStyle = (isExpanded: boolean) => ({
  marginTop: 6,
  position: 'relative',
  overflow: isExpanded ? 'visible' : 'hidden',
  maxHeight: isExpanded ? 'auto' : 40,
  a: {
    color: Colors[hintColor],
    textDecoration: 'underline !important',
  },
  button: {
    outline: 'none',
  },
  'p:first-of-type': {
    marginBottom: 0,
  },
  'p:last-of-type': {
    marginBottom: 0,
  },
  code: {
    backgroundColor: 'rgba(27, 31, 35, .04)',
    border: 'none',
    borderRadius: 3,
    color: Colors.batmobile,
    fontSize: '98%',
    padding: '.2em .4em',
  },
});

const hintTextContainerStyle = {
  overflow: 'hidden',
  maxWidth: '100%',
};

const expandHintTextContainerStyle = ({
  isExpanded,
}: {
  isExpanded: boolean,
}) => {
  const commonStyles = {
    backgroundColor: Colors.white,
  };

  if (isExpanded) {
    // We can't use absolute position or else some text will be cut off.
    return {
      ...commonStyles,
      display: 'flex',
      justifyContent: 'flex-end',
    };
  }

  return {
    ...commonStyles,
    position: 'absolute',
    right: 0,
    bottom: 0,
  };
};

const expandablePrefixStyle = {
  margin: '0 5px',
};

class ExpandableFieldHelp extends Component<Props, State> {
  state = {
    isExpanded: false,
    shouldBeExpandable: false,
  };

  containerRef: Ref<HTMLElement> = createRef();

  onToggleExpansion = (event: SyntheticEvent<>) => {
    event.preventDefault();
    this.setState({ isExpanded: !this.state.isExpanded });
  };

  componentDidMount() {
    this.updateExpandability();
  }

  componentDidUpdate() {
    this.updateExpandability();
  }

  updateExpandability() {
    const container = this.containerRef.current || {};
    const { clientHeight, scrollHeight } = container;
    const shouldBeExpandable = scrollHeight > clientHeight;
    if (this.state.shouldBeExpandable !== shouldBeExpandable) {
      this.setState({ shouldBeExpandable });
    }
  }

  render() {
    const { helpText } = this.props;
    const { isExpanded } = this.state;

    const needsExpansionControl =
      this.state.shouldBeExpandable || this.state.isExpanded;

    if (!helpText) {
      return null;
    }

    return (
      <div css={containerStyle(isExpanded)} ref={this.containerRef}>
        <div css={hintTextContainerStyle}>
          <div>
            <Text color={hintColor} type="fine">
              <span
                dangerouslySetInnerHTML={{
                  __html: helpText,
                }}
              />
            </Text>
          </div>
        </div>

        {needsExpansionControl && (
          <div css={expandHintTextContainerStyle({ isExpanded })}>
            <Button kind="as-text" onClick={this.onToggleExpansion}>
              {isExpanded ? (
                <span css={expandablePrefixStyle}>
                  <Icon name="arrow-up" size={10} />
                </span>
              ) : (
                <span css={{ ...expandablePrefixStyle, lineHeight: '18px' }}>
                  ...
                </span>
              )}
              <Text color={hintColor} type="fine">
                {isExpanded ? 'less' : 'more'}
              </Text>
            </Button>
          </div>
        )}
      </div>
    );
  }
}

export default ExpandableFieldHelp;
